import {useCallback} from 'react';
import {
    EventAction,
    EventCategory,
    EventCurrencyCodes,
    EventLabel,
    EventNames,
    OWOX_PAGES,
} from 'modules/google-analytics/constants';
import {useGtagEventLocation} from '../helpers/useGtagEventLocation';
import {IPremiumServiceBanner} from 'modules/home/models/PremiumService';
import {ICatalogBanner} from 'modules/products/types';
import {useBannerViewTracker} from './useBannerViewTracker';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';

type TBannerViewType = 'common' | 'premium' | 'stock';

interface ICommonBanner extends ICatalogBanner {
    id: string;
    name?: string;
    url?: string;
    hint?: {text: string; bgColor: string};
    linkTarget?: string;
    images?: {
        desktop: string;
        tablet: string;
        mobile: string;
    };
    position?: number;
}

export interface ICommonBannerWithPosition extends ICommonBanner {
    position: number;
}

interface IUseBannerViewGtagParams {
    type: TBannerViewType;
    banners?: Partial<ICommonBannerWithPosition>[];
    premiumBanner?: IPremiumServiceBanner;
    eventLocation?: keyof typeof OWOX_PAGES;
    creative?: 'main_slider' | 'promo_page_main' | 'promo_page_another_promo' | 'promo_page_trust' | 'snippet__banner';
    enableTracking?: boolean;
}

export const useBannerViewGtag = () => {
    const {eventLocation: defaultLocation} = useGtagEventLocation();
    const {isBannerViewed, markBannerAsViewed} = useBannerViewTracker();

    return useCallback(
        // eslint-disable-next-line complexity
        (params: Partial<IUseBannerViewGtagParams>) => {
            const {banners = [], creative, eventLocation = defaultLocation, enableTracking = false} = params;

            if ('common' === params.type && params.banners) {
                let bannersToSend = banners;

                // фильтруем уже просмотренные баннеры
                if (enableTracking) {
                    bannersToSend = banners.filter((banner) => !isBannerViewed(banner));

                    // все баннеры уже были просмотрены
                    if (0 === bannersToSend.length) {
                        return;
                    }
                }

                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventLabel: EventLabel.Banner,
                    eventContent: bannersToSend[0].sanitizedTitle ?? bannersToSend[0].name ?? 'null',
                    eventPosition: bannersToSend[0].position || 1,
                    eventLocation,
                    eventCategory: EventCategory.NonInteractions,
                    eventAction: EventAction.Show,
                    ecommerce: {
                        currencyCode: EventCurrencyCodes.RUB,
                        promoView: {
                            promotions: bannersToSend.map((banner) => ({
                                id: banner.id,
                                name: banner.sanitizedTitle ?? banner.name ?? 'null',
                                creative,
                                position: banner.position || 1,
                            })),
                        },
                    },
                });

                // отмечаем баннеры как просмотренные
                if (enableTracking) {
                    bannersToSend.forEach((banner) => {
                        markBannerAsViewed(banner);
                    });
                }
            }

            if ('stock' === params.type && params.banners) {
                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventLabel: EventLabel.Banner,
                    eventContent: banners[0].sanitizedTitle ?? banners[0].name ?? 'null',
                    eventPosition: banners[0].position || 1,
                    eventLocation,
                    eventCategory: EventCategory.NonInteractions,
                    eventAction: EventAction.Show,
                    ecommerce: {
                        currencyCode: EventCurrencyCodes.RUB,
                        promoView: {
                            promotions: banners.map((banner) => ({
                                id: banner.id,
                                name: banner.sanitizedTitle ?? banner.name ?? 'null',
                                creative,
                                position: banner.position || 1,
                            })),
                        },
                    },
                });
            }

            if ('premium' === params.type && params.premiumBanner) {
                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventCategory: EventCategory.NonInteractions,
                    eventAction: EventAction.Show,
                    eventLabel: EventLabel.PriveCard,
                    eventValue: params.premiumBanner.button.link,
                    eventContent: params.premiumBanner.title,
                    eventLocation,
                });
            }
        },
        [defaultLocation, isBannerViewed, markBannerAsViewed],
    );
};
